import { createFileRoute } from "@tanstack/react-router";
import type { ClientStudioDocHydrated } from "api-types/studioDocs/studioDocResponses";
import { buildingIsEmpty } from "building-generation/building/buildingIsEmpty";
import type { OptionChoices } from "building-types/options/optionChoices";
import { WEB_API_BASE_PATH } from "common/constants";
import { assertIsNotNull } from "common/utils/assertOfType";
import { toTitleCase } from "common/utils/strings";
import docMarshal from "doc/docMarshal";
import { HighlightDataTexture } from "gfx/HighlightDataTexture";
import { freeThreeObject3D } from "gfx/threejs/higharc/clearThreeScene";
import type { THREEViewerBase } from "gfx/THREEViewerBase";
import { sheetBackgroundColor } from "gfx/THREEViewerScene";
import useGenerateBuilding from "lib/components/design/useGenerateBuilding";
import GPURequiredContainer from "lib/components/gfx/GPURequiredContainer";
import Page from "lib/components/layout/Page";
import { useShowroomBasePlanData } from "lib/components/showroom2/data/useShowroomAPI";
import usePlanSetFonts from "lib/components/studio/editorViewer/usePlanSetFonts";
import useTHREEViewerLite from "lib/components/studio/editorViewer/useTHREEViewerLite";
import useOrthoZoomToFit from "lib/components/studio/useOrthoZoomToFit";
import { useFetch } from "lib/fetcher";
import { PlanSetBSPProvider } from "lib/house/pdf/planSetBSPProvider";
import { SingleBuildingPlanSetComputeProvider } from "lib/house/pdf/planSetComputeProvider";
import { makePlanPagesForBuiltinPlanSetTemplate } from "lib/house/plans/planPages";
import { type PlanPages } from "lib/house/plans/planPagesTypes";
import { THREEPlanSetRenderer } from "lib/house/plans/threePlanSetRenderer";
import { optionChoicesNew } from "lib/options/optionChoicesNew";
import { useAppRouterParams } from "lib/routeHelpers";
import { useEffect, useMemo, useRef, useState } from "react";
import ProductName from "ui/components/ProductName";
import type { OptionProps } from "ui/forms/Select";
import Select from "ui/forms/Select";
import useBroadcastChannelSync from "ui/hooks/useBroadcastChannelSync";
import Header from "ui/layouts/Header";
import withAuthentication from "../../components/withAuthentication";
import styles from "./sheets.module.scss";

const highlightDataTexture = new HighlightDataTexture();

const DEFAULT_PAGE_FILTER = 0;

function SheetsPage() {
  const [optionChoices, setOptionChoices] = useState<OptionChoices>({});
  const [pages, setPages] = useState<PlanPages | null>(null);
  const [pageFilter, setPageFilter] = useState(DEFAULT_PAGE_FILTER); // show the first page by default

  const startingPageMounted = useRef(false);

  const { id: studioDocId, builderId } = useAppRouterParams();

  const fetchKey = `${WEB_API_BASE_PATH}/studio-docs/${studioDocId}${builderId ? `&builderId=${builderId}` : ""}`;

  const { data: studioDoc } = useFetch<ClientStudioDocHydrated>(fetchKey, [studioDocId], {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { data: planData } = useShowroomBasePlanData();

  // Use either the studioDoc or planData, depending on which params are provided
  const doc = useMemo(() => {
    if (!planData && !studioDoc) {
      return null;
    }
    const doc = docMarshal(planData?.homePlan?.doc || studioDoc?.doc);

    const { optionChoices } = optionChoicesNew(doc);
    setOptionChoices(optionChoices);

    return doc;
  }, [studioDoc, planData]);

  assertIsNotNull(doc);
  const { building } = useGenerateBuilding(doc, optionChoices);

  const viewerRef = useRef<THREEViewerBase>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);

  const options: OptionProps[] = useMemo(() => {
    if (!pages?.pages?.length) {
      return [];
    }
    return pages.pages.map((page, i) => ({
      value: i,
      label: toTitleCase(page.sheetTitle.name.toLocaleLowerCase()).replace(/[\r\n]+/g, " "),
    }));
  }, [pages]);

  const channelId = studioDocId || planData?.masterPlan?.studioDocId;
  useBroadcastChannelSync({
    state: optionChoices,
    setState: setOptionChoices,
    channelId,
    channelName: "optionChoices",
  });
  //  broadcasting select "options" because PlanPages cannot be serialized
  useBroadcastChannelSync({
    state: options,
    channelId,
    channelName: "demo/sheets_pages",
    requireFocus: false,
  });
  useBroadcastChannelSync({
    state: pageFilter,
    setState: setPageFilter,
    channelId,
    channelName: "demo/sheets_pageFilter",
  });

  const fontsLoaded = usePlanSetFonts();

  useTHREEViewerLite({
    viewerRef,
    viewerContainerRef,
    options: { highlightDataTexture },
    viewName: "sheets",
  });

  // get default starting page from params
  useEffect(() => {
    if (!pages) {
      return;
    }
  }, [pages]);

  // when the building is updated, we need to rebuild the renderer and pages
  useEffect(() => {
    if (!building || buildingIsEmpty(building) || !fontsLoaded) {
      return;
    }

    void (async () => {
      // Make pages
      const pages = await makePlanPagesForBuiltinPlanSetTemplate({
        building: building,
        faceCategories: undefined,
      });

      setPages(pages);

      // Set the starting page based on url param
      const params = new URLSearchParams(window.location.search);
      if (!startingPageMounted.current && params.get("page") && pageFilter === DEFAULT_PAGE_FILTER) {
        const page = pages.pages.findIndex((p) => p.sheetTitle.name.replace(/[\r\n]+/g, " ") === params.get("page"));
        if (page > 0) {
          setPageFilter(page);
        }
        // only set it once, so it doesnt override user selected page
        startingPageMounted.current = true;
      }
    })();
  }, [building, fontsLoaded]);

  const { zoomToFitBuiltinPlanSetPage } = useOrthoZoomToFit();

  // when the pages or page filter changes, we need to re-render the pages
  useEffect(() => {
    if (!pages || !building || buildingIsEmpty(building)) {
      return;
    }

    assertIsNotNull(viewerRef.current);
    const viewer = viewerRef.current;
    let aborted = false;

    const bspProvider = new PlanSetBSPProvider(building);

    void (async () => {
      const renderer = new THREEPlanSetRenderer(
        undefined,
        building,
        new SingleBuildingPlanSetComputeProvider(building, bspProvider),
        undefined,
        viewer,
      );

      const finalPages = {
        ...pages,
        pages: pages.pages.filter((_, i) => pageFilter === -1 || i === pageFilter),
      };

      viewer.setSceneBackground(sheetBackgroundColor);
      viewer.grid2DVisible = false;
      const rootGroup = await renderer.renderPages(finalPages);

      if (aborted) {
        freeThreeObject3D(rootGroup);
        return;
      }

      viewer.add(rootGroup);

      zoomToFitBuiltinPlanSetPage(viewer, "builtinPlanSet", finalPages.pages[0], 0);
    })();

    return () => {
      aborted = true;
      viewer.clear();
      bspProvider.dispose();
    };
  }, [pages, pageFilter]);

  return (
    <Page pageTitle="Live Sheets Preview" className={styles.plans}>
      <Header
        className={styles.header}
        left={<ProductName productName="Live Sheets" />}
        right={
          <div>
            {options.length > 1 && (
              <Select
                selectSize="medium"
                value={pageFilter}
                onChange={(value) => setPageFilter(value as number)}
                options={options}
              />
            )}
          </div>
        }
      />
      <div className={styles.viewerWrapper}>
        <GPURequiredContainer>
          <div className={styles.viewer} ref={viewerContainerRef} tabIndex={-1} />
        </GPURequiredContainer>
      </div>
    </Page>
  );
}

// TODO: to enable this for public sandbox, will need to replace studio fetch endpoint with a public access one
export const Route = createFileRoute("/demo/sheets")({
  component: withAuthentication(SheetsPage, { superAdmin: true }),
});
